import { useEffect, useState, useCallback } from 'react';
import { listAdminPurchaseOrders, downloadAdminPurchaseOrderFile } from '@/services/portalPurchaseOrders';
import { FileUp, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export default function AdminPurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listAdminPurchaseOrders();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'Liste yüklenemedi.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDownload = async (row) => {
    const id = row._id;
    setDownloadingId(id);
    setError('');
    try {
      await downloadAdminPurchaseOrderFile(id, row.file_name);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'İndirme başarısız.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Purchase orders</h1>
          <p className="text-slate-500 text-sm mt-0.5">Müşterilerin yüklediği PO dosyaları</p>
        </div>
        <Button type="button" variant="outline" onClick={load} disabled={loading}>
          Yenile
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-500 text-sm">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileUp className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Henüz yüklenmiş purchase order yok.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[800px]" data-testid="admin-po-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Müşteri</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">E-posta</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Reference</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Dosya</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Durum</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Yüklenme</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((row) => (
                <tr key={row._id} className="hover:bg-slate-50/80">
                  <td className="px-5 py-4 text-sm font-medium text-slate-900">{row.customer_name || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{row.customer_email || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{row.reference || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-700 max-w-[200px] truncate" title={row.file_name}>
                    {row.file_name || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm capitalize text-slate-600">{row.status || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">
                    {formatDate(row.uploaded_at ?? row.created_at)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      disabled={downloadingId === row._id}
                      onClick={() => onDownload(row)}
                      data-testid={`admin-po-download-${row._id}`}
                    >
                      {downloadingId === row._id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Görüntüle / indir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
